import { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button, Input } from '@lms/ui';
import type { CreateWhatsAppGroupInput, UpdateWhatsAppGroupInput } from '@lms/api-client';
import type { WhatsAppGroup, WhatsAppGender } from '@lms/shared/types';
import { useCreateWhatsAppGroup, useUpdateWhatsAppGroup } from '../hooks/useWhatsAppGroups';
import { usePrograms } from '../hooks/usePrograms';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface WhatsAppGroupModalProps {
  group?: WhatsAppGroup;
  initialProgramId?: string;
  onClose: () => void;
}

interface WhatsAppGroupFormState {
  programId: string;
  name: string;
  gender: WhatsAppGender;
  inviteLink: string;
  maxMembers: number;
  ageMin?: number;
  ageMax?: number;
  description: string;
}

function createInitialFormState(
  group: WhatsAppGroup | undefined,
  initialProgramId: string | undefined
): WhatsAppGroupFormState {
  return {
    programId: group?.programId ?? initialProgramId ?? '',
    name: group?.name ?? '',
    description: group?.description ?? '',
    gender: group?.gender ?? 'MIXED',
    maxMembers: group?.maxMembers ?? 1000,
    inviteLink: group?.inviteLink ?? '',
    ageMin: group?.ageMin,
    ageMax: group?.ageMax,
  };
}

function getSaveErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

export function WhatsAppGroupModal({ group, initialProgramId, onClose }: WhatsAppGroupModalProps) {
  const isEditing = !!group;
  const { t } = useLanguage();
  const { mutate: create, isPending: isCreating } = useCreateWhatsAppGroup();
  const { mutate: update, isPending: isUpdating } = useUpdateWhatsAppGroup();
  const { data: programsData } = usePrograms({ limit: 100 });
  const programItems = Array.isArray(programsData?.items) ? programsData.items : [];

  const [formData, setFormData] = useState<WhatsAppGroupFormState>(() =>
    createInitialFormState(group, initialProgramId)
  );
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const name = formData.name.trim();
    const inviteLink = formData.inviteLink.trim();
    const description = formData.description.trim();
    if (!name || (!isEditing && !formData.programId)) {
      setFormError(t(copy.whatsAppGroupModal.requiredFieldsError));
      return;
    }
    if (!Number.isInteger(formData.maxMembers) || formData.maxMembers < 1) {
      setFormError(t(copy.whatsAppGroupModal.capacityError));
      return;
    }
    if (
      formData.ageMin !== undefined &&
      formData.ageMax !== undefined &&
      formData.ageMin > formData.ageMax
    ) {
      setFormError(t(copy.whatsAppGroupModal.ageRangeError));
      return;
    }
    if (inviteLink) {
      try {
        const url = new URL(inviteLink);
        if (url.protocol !== 'https:') throw new Error('insecure-url');
      } catch {
        setFormError(t(copy.whatsAppGroupModal.inviteLinkError));
        return;
      }
    }

    const editableData: UpdateWhatsAppGroupInput = {
      name,
      gender: formData.gender,
      maxMembers: formData.maxMembers,
      ageMin: formData.ageMin ?? null,
      ageMax: formData.ageMax ?? null,
      inviteLink: inviteLink || null,
      description: description || null,
    };

    if (isEditing && group) {
      update(
        { id: group.id, data: editableData },
        {
          onSuccess: () => onClose(),
          onError: error =>
            setFormError(getSaveErrorMessage(error, t(copy.whatsAppGroupModal.saveError))),
        }
      );
    } else {
      const createData: CreateWhatsAppGroupInput = {
        programId: formData.programId,
        name,
        gender: formData.gender,
        maxMembers: formData.maxMembers,
        ...(formData.ageMin !== undefined ? { ageMin: formData.ageMin } : {}),
        ...(formData.ageMax !== undefined ? { ageMax: formData.ageMax } : {}),
        ...(inviteLink ? { inviteLink } : {}),
        ...(description ? { description } : {}),
      };
      create(createData, {
        onSuccess: () => onClose(),
        onError: error =>
          setFormError(getSaveErrorMessage(error, t(copy.whatsAppGroupModal.saveError))),
      });
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-50 flex justify-center p-4 animate-fadeIn overflow-y-auto py-12">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="whatsapp-group-modal-title"
        className="bg-surface-container-lowest rounded-[2rem] w-full max-w-xl shadow-level4 overflow-hidden animate-slideUp my-auto"
      >
        <div className="p-8 bg-surface-container-low/30 flex justify-between items-center">
          <div>
            <h2
              id="whatsapp-group-modal-title"
              className="text-2xl font-headline font-bold text-on-surface m-0 tracking-tight"
            >
              <Bilingual
                {...(isEditing
                  ? copy.whatsAppGroupModal.titleEdit
                  : copy.whatsAppGroupModal.titleCreate)}
              />
            </h2>
            <p className="text-xs font-body text-on-surface-variant mt-1 opacity-60 uppercase tracking-widest font-bold">
              <Bilingual {...copy.whatsAppGroupModal.subtitle} />
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t(copy.common.cancel)}
            className="p-2 hover:bg-on-surface/10 rounded-full transition-colors"
          >
            <X size={20} className="text-on-surface-variant" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {formError && (
            <div
              role="alert"
              className="rounded-2xl bg-error-container px-4 py-3 text-sm font-body text-on-error-container"
            >
              {formError}
            </div>
          )}
          <div className="space-y-1.5">
            <label
              htmlFor="whatsapp-group-name"
              className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60"
            >
              <Bilingual {...copy.whatsAppGroupModal.labelName} />
            </label>
            <Input
              id="whatsapp-group-name"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder={t(copy.whatsAppGroupModal.placeholderName)}
              className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label
                htmlFor="whatsapp-group-program"
                className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60"
              >
                <Bilingual {...copy.whatsAppGroupModal.labelProgram} />
              </label>
              <select
                id="whatsapp-group-program"
                className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none appearance-none h-12"
                value={formData.programId}
                onChange={e => setFormData({ ...formData, programId: e.target.value })}
                disabled={isEditing}
                required
              >
                <option value="">{t(copy.whatsAppGroupModal.selectProgram)}</option>
                {programItems.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {isEditing && (
                <p className="m-0 pl-2 text-[10px] text-on-surface-variant/70">
                  <Bilingual {...copy.whatsAppGroupModal.programLocked} />
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="whatsapp-group-gender"
                className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60"
              >
                <Bilingual {...copy.whatsAppGroupModal.labelGender} />
              </label>
              <select
                id="whatsapp-group-gender"
                className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none appearance-none h-12"
                value={formData.gender}
                onChange={e =>
                  setFormData({ ...formData, gender: e.target.value as WhatsAppGender })
                }
                required
              >
                <option value="MIXED">{t(copy.whatsAppGroupModal.genderMixed)}</option>
                <option value="IKHWAN">{t(copy.whatsAppGroupModal.genderIkhwan)}</option>
                <option value="AKHWAT">{t(copy.whatsAppGroupModal.genderAkhwat)}</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="whatsapp-group-invite-link"
              className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60"
            >
              <Bilingual {...copy.whatsAppGroupModal.labelInviteLink} />
            </label>
            <Input
              id="whatsapp-group-invite-link"
              type="url"
              value={formData.inviteLink || ''}
              onChange={e => setFormData({ ...formData, inviteLink: e.target.value })}
              placeholder={t(copy.whatsAppGroupModal.placeholderInviteLink)}
              className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label
                htmlFor="whatsapp-group-capacity"
                className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60"
              >
                <Bilingual {...copy.whatsAppGroupModal.labelCapacity} />
              </label>
              <Input
                id="whatsapp-group-capacity"
                type="number"
                min={1}
                value={formData.maxMembers}
                onChange={e =>
                  setFormData({
                    ...formData,
                    maxMembers: Number(e.target.value),
                  })
                }
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
                required
              />
              <p className="text-[10px] text-on-surface-variant/70 pl-2 m-0">
                <Bilingual {...copy.whatsAppGroupModal.capacityHelp} />
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label
                htmlFor="whatsapp-group-age-min"
                className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60"
              >
                <Bilingual {...copy.whatsAppGroupModal.labelAgeMin} />
              </label>
              <Input
                id="whatsapp-group-age-min"
                type="number"
                min={0}
                max={150}
                value={formData.ageMin ?? ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    ageMin: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                placeholder={t(copy.whatsAppGroupModal.placeholderAge)}
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="whatsapp-group-age-max"
                className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60"
              >
                <Bilingual {...copy.whatsAppGroupModal.labelAgeMax} />
              </label>
              <Input
                id="whatsapp-group-age-max"
                type="number"
                min={0}
                max={150}
                value={formData.ageMax ?? ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    ageMax: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                placeholder={t(copy.whatsAppGroupModal.placeholderAge)}
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="whatsapp-group-description"
              className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60"
            >
              <Bilingual {...copy.whatsAppGroupModal.labelDescription} />
            </label>
            <textarea
              id="whatsapp-group-description"
              className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none min-h-[100px] resize-none"
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder={t(copy.whatsAppGroupModal.placeholderDescription)}
            />
          </div>

          <div className="pt-6 flex justify-end gap-6 items-center">
            <button
              type="button"
              onClick={onClose}
              className="text-[10px] uppercase font-black tracking-[0.2em] text-on-surface-variant hover:text-primary transition-colors"
            >
              <Bilingual {...copy.whatsAppGroupModal.abort} />
            </button>
            <Button
              type="submit"
              className="rounded-2xl px-10 h-14 text-[10px] uppercase font-black tracking-[0.25em] shadow-level2"
              disabled={isPending}
            >
              {isPending ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              <Bilingual
                {...(isEditing
                  ? copy.whatsAppGroupModal.submitEdit
                  : copy.whatsAppGroupModal.submitCreate)}
              />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
